import React from 'react'
import clsx from 'clsx'
import { useAppSelector } from '@SonarCX/hooks/redux'
import { Add, ArrowLeft2, ArrowRight2, Edit2 } from 'iconsax-react'
import { useTheme } from '@mui/material'

import TableSettings from '../TableSettings'

import { selectCategory } from '@SonarCX/slices/category'
import useCombinedTableState from '@SonarCX/hooks/useCombinedTableState'
import { useBreakpoint } from '@SonarCX/providers/BreakpointProvider'
import useStyles from './styles'

const TaskHeader = ({
  fakePagination,
  onEditClick,
  isInfinite = false,
  totalData,
  canAdd,
  createTask,
  loaded,
  children
}) => {
  const theme = useTheme()

  const { categories } = useAppSelector(selectCategory)

  const {
    expanded,
    toggleExpanded,
    name,
    tasks,
    prev,
    next,
    total,
    paginated,
    visible,
    key
  } = useCombinedTableState()
  const { isSmall, down450 } = useBreakpoint()

  const [hovering, setHovering] = React.useState(false)
  const [opened, setOpened] = React.useState(!Boolean(toggleExpanded))

  const classes = useStyles({ expanded })

  const halt = React.useCallback(
    func => e => {
      e.preventDefault()
      e.stopPropagation()
      func()
    },
    []
  )

  React.useEffect(() => {
    if (expanded) {
      setOpened(true)
    }
  }, [expanded])

  const count = React.useMemo(
    () => total ?? totalData ?? tasks.length,
    [total, totalData, tasks.length]
  )
  const startingCount = React.useMemo(
    () => categories[key]?.total_open ?? 0,
    [categories, key]
  )

  return (
    <div className={classes.root} data-testid={`${name}-task-header`}>
      <div
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        className={classes.header}
      >
        <div className={classes.title}>
          {toggleExpanded && (
            <ArrowRight2
              size={theme.size.base}
              variant='Bold'
              onClick={toggleExpanded}
              className='icon'
            />
          )}

          <div className={clsx(classes.name, 'taskHeaderName')}>{name}</div>

          {(total > 0 || expanded) && isInfinite && totalData ? (
            <div
              className={classes.count}
            >{`${tasks.length} / ${totalData}`}</div>
          ) : (
            <div className={classes.count}>
              {paginated ? `${visible} / ` : ''}
              {opened && loaded ? count : startingCount}
            </div>
          )}

          {hovering && onEditClick && (
            <Edit2
              size={theme.size.base}
              onClick={halt(onEditClick)}
              className={classes.edit}
            />
          )}

          {canAdd && createTask && (
            <div className={classes.add}>
              <div
                onClick={halt(createTask)}
                className={classes.addButton}
                data-testid={`${name}-task-header-button`}
              >
                <Add size={theme.size.lg} className='icon' /> Task
              </div>
            </div>
          )}

          {fakePagination && (prev || next) && expanded && (
            <div
              className={classes.nav}
              style={{
                right: isSmall
                  ? prev && next
                    ? '0.75rem'
                    : prev
                    ? '0.75rem'
                    : '0'
                  : prev && next
                  ? '2.75rem'
                  : prev
                  ? '2.75rem'
                  : '2rem'
              }}
            >
              {prev && (
                <div onClick={halt(prev)}>
                  <ArrowLeft2 size={theme.size.base} variant='Bold' />

                  <span>{down450 ? '' : 'PREVIOUS PAGE'}</span>
                </div>
              )}

              {next && (
                <div onClick={halt(next)}>
                  <span>{down450 ? '' : 'NEXT PAGE'}</span>

                  <ArrowRight2 size={theme.size.base} variant='Bold' />
                </div>
              )}
            </div>
          )}

          {children}

          {!isSmall && <TableSettings id={key} />}
        </div>
      </div>
    </div>
  )
}

export default TaskHeader
